import React, { useEffect, useState } from "react";
import styles from "./result.module.css";

const STEP_MS = 650;

// Phones get a vertical stepper: four columns are too narrow for the labels and dates.
// Same breakpoint as the rest of the page (result.module.css).
const VERTICAL_QUERY = "(max-width: 559px)";

const matches = (query) => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(query).matches;

const useMediaQuery = (query) => {
  const [match, setMatch] = useState(() => matches(query));
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const onChange = (e) => setMatch(e.matches);
    setMatch(mq.matches);
    // Safari < 14 only has addListener.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange));
  }, [query]);
  return match;
};

const ICONS = {
  placed: (
    <>
      <path d="M21 8l-9-5-9 5 9 5 9-5z" />
      <path d="M3 8v8l9 5 9-5V8" />
      <path d="M12 13v8" />
    </>
  ),
  shipped: (
    <>
      <path d="M2.5 6.5h11v10h-11z" />
      <path d="M13.5 10h4l3.5 3.5v3h-7.5" />
      <circle cx="6.5" cy="17.5" r="1.8" />
      <circle cx="17" cy="17.5" r="1.8" />
    </>
  ),
  ofd: (
    <>
      <circle cx="5.5" cy="17" r="2.5" />
      <circle cx="18.5" cy="17" r="2.5" />
      <path d="M8 17h6.5l2.5-6h-4.5" />
      <path d="M14 5.5h2.5L19 14.5" />
      <path d="M3 11h6v3H3z" />
    </>
  ),
  delivered: (
    <>
      <path d="M3.5 10.5L12 4l8.5 6.5V20h-17z" />
      <path d="M8.5 14l2.5 2.5 4.5-4.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12.5" r="8" />
      <path d="M12 8.5v4l2.5 2" />
      <path d="M9.5 2.5h5" />
    </>
  ),
  fail: (
    <>
      <path d="M3.5 10.5L12 4l8.5 6.5V20h-17z" />
      <path d="M9.5 12.5l5 5M14.5 12.5l-5 5" />
    </>
  ),
};

// Fills the track step by step (design: 120 ms delay, then 650 ms per step).
// onCelebrate fires once the last step of a delivered order is reached.
const StatusStepper = ({ steps, cur, finished, tone, onCelebrate }) => {
  const [anim, setAnim] = useState({ fill: 0, reached: -1, tail: 0, instant: true, celebrate: false });
  const n = steps.length;
  const vertical = useMediaQuery(VERTICAL_QUERY);

  useEffect(() => {
    setAnim({ fill: 0, reached: -1, tail: 0, instant: true, celebrate: false });
    if (cur < 0 || n < 2) return undefined;
    const timers = [setTimeout(() => setAnim((a) => ({ ...a, instant: false, fill: cur / (n - 1), reached: 0 })), 120)];
    for (let i = 1; i <= cur; i++) timers.push(setTimeout(() => setAnim((a) => ({ ...a, reached: i })), 120 + i * STEP_MS));
    timers.push(setTimeout(() => setAnim((a) => ({ ...a, tail: 1 })), 120 + cur * STEP_MS + 150));
    if (finished) {
      timers.push(
        setTimeout(() => {
          setAnim((a) => ({ ...a, celebrate: true }));
          onCelebrate && onCelebrate();
        }, 120 + cur * STEP_MS + 200)
      );
    }
    return () => timers.forEach(clearTimeout);
    // onCelebrate is read when the timer fires; restarting the reveal for a new callback is not wanted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur, n, finished]);

  if (!n) return null;
  const inset = `${50 / n}%`;
  const exception = tone !== "ok";

  return (
    <div className={[styles.stepper, vertical && styles.vertical].filter(Boolean).join(" ")} data-tone={tone}>
      {/* Horizontal: one track behind all the dots. Vertical: one segment under each dot (below). */}
      {!vertical && (
        <div className={styles.track} style={{ left: inset, right: inset }}>
          <div
            className={styles.fill}
            style={{ width: `${anim.fill * 100}%`, transition: anim.instant ? "none" : `width ${cur * STEP_MS}ms linear, background .5s ease` }}
          >
            <span className={styles.sheen} />
          </div>
          <div className={styles.tail} style={{ left: `${anim.fill * 100}%`, opacity: finished ? 0 : anim.tail }} />
        </div>
      )}
      <div className={styles.steps} style={vertical ? undefined : { gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
        {steps.map((s, i) => {
          const done = i <= anim.reached;
          const isCur = i === cur && anim.reached >= cur;
          const d = i - cur;
          const live = isCur && !finished;
          const opacity = d <= 0 || done ? 1 : Math.max(0.38, 1 - d * 0.28);
          return (
            <div className={styles.step} key={s.key} aria-current={i === cur ? "step" : undefined}>
              <span className={styles.rail}>
                <span
                  className={[styles.dot, done && styles.dotDone, !done && d === 1 && styles.dotNext, live && styles.dotLive].filter(Boolean).join(" ")}
                  style={{ transform: `scale(${done ? (live ? 1.08 : 1) : 0.88})` }}
                >
                  {finished && i === cur && anim.celebrate && (
                    <>
                      <span className={styles.burst} />
                      <span className={`${styles.burst} ${styles.burstLate}`} />
                    </>
                  )}
                  <span className={live && !exception ? styles.bob : styles.iconWrap} style={{ opacity }}>
                    <svg className={styles.stepIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={s.icon === "clock" || s.icon === "fail" ? 2 : 1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      {ICONS[s.icon]}
                    </svg>
                  </span>
                </span>
                {vertical && i < n - 1 && (
                  // Segment i joins dot i to dot i+1. It fills while the reveal moves from i to
                  // i+1, so the vertical reveal keeps the horizontal timing (STEP_MS per step).
                  <span className={styles.seg}>
                    <span
                      className={styles.segFill}
                      style={{
                        height: i < cur && anim.reached >= i ? "100%" : "0%",
                        transition: anim.instant ? "none" : `height ${STEP_MS}ms linear, background .5s ease`,
                      }}
                    />
                    {i === cur && !finished && <span className={styles.segTail} style={{ opacity: anim.tail }} />}
                  </span>
                )}
              </span>
              <div className={styles.stepText} style={{ opacity }}>
                <span className={styles.stepLabel} style={{ color: done ? "#111827" : "#5B6478" }}>
                  {s.label}
                </span>
                {s.note && isCur && <span className={styles.stepNote}>{s.note}</span>}
                {s.date && done && <span className={styles.stepMeta}>{s.date}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StatusStepper;
